import { useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, Waves, Radio, Ship, Crown, CloudSun, X, AlertTriangle, Zap, ClipboardCheck, Wrench, GraduationCap, Users, Target, BookOpen } from "lucide-react";
import { CaptainRocaAI } from "./CaptainRocaAI";
import { useIsMobile } from "@/hooks/use-mobile";
import { CaptainLogo } from "./CaptainLogo";

type Action =
  | { id: "captain"; kind: "chat"; label: string; color: string; activeBg: string; activeBorder: string; activeGlow: string }
  | { id: string; kind: "link"; href: string; icon: React.ComponentType<{ className?: string }>; label: string; color: string; activeBg: string; activeBorder: string; activeGlow: string };

const actions: Action[] = [
  {
    id: "captain", kind: "chat", label: "ROCA",
    color: "text-accent", activeBg: "bg-accent/15", activeBorder: "border-accent",
    activeGlow: "shadow-[0_0_16px_hsl(var(--accent)/0.4)]",
  },
  {
    id: "cotier", kind: "link", href: "/permis-academy", icon: Anchor, label: "Côtier",
    color: "text-primary", activeBg: "bg-primary/15", activeBorder: "border-primary",
    activeGlow: "shadow-[0_0_16px_hsl(var(--primary)/0.4)]",
  },
  {
    id: "fluvial", kind: "link", href: "/permis-academy#fluvial", icon: Waves, label: "Fluvial",
    color: "text-electric-blue", activeBg: "bg-electric-blue/15", activeBorder: "border-electric-blue",
    activeGlow: "shadow-[0_0_16px_hsl(var(--electric-blue)/0.4)]",
  },
  {
    id: "crr", kind: "link", href: "/crr-zone1", icon: Radio, label: "CRR",
    color: "text-neon-cyan", activeBg: "bg-neon-cyan/15", activeBorder: "border-neon-cyan",
    activeGlow: "shadow-[0_0_16px_hsl(var(--neon-cyan)/0.4)]",
  },
  {
    id: "quizz", kind: "link", href: "/permis-academy#quiz-cotier", icon: ClipboardCheck, label: "Quizz",
    color: "text-sky-400", activeBg: "bg-sky-400/15", activeBorder: "border-sky-400",
    activeGlow: "shadow-[0_0_16px_rgba(56,189,248,0.4)]",
  },
  {
    id: "briefing", kind: "link", href: "/briefings", icon: BookOpen, label: "Briefing",
    color: "text-fuchsia-400", activeBg: "bg-fuchsia-400/15", activeBorder: "border-fuchsia-400",
    activeGlow: "shadow-[0_0_16px_rgba(232,121,249,0.4)]",
  },
  {
    id: "bpjeps", kind: "link", href: "/bpjeps-motonautisme", icon: Ship, label: "BPJEPS",
    color: "text-neon-purple", activeBg: "bg-neon-purple/15", activeBorder: "border-neon-purple",
    activeGlow: "shadow-[0_0_16px_hsl(var(--neon-purple)/0.4)]",
  },
  {
    id: "uc1", kind: "link", href: "/bpjeps-master-builder#bc1", icon: Users, label: "UC1",
    color: "text-sky-400", activeBg: "bg-sky-400/15", activeBorder: "border-sky-400",
    activeGlow: "shadow-[0_0_16px_rgba(56,189,248,0.4)]",
  },
  {
    id: "uc2", kind: "link", href: "/bpjeps-master-builder#bc2", icon: Target, label: "UC2",
    color: "text-violet-400", activeBg: "bg-violet-400/15", activeBorder: "border-violet-400",
    activeGlow: "shadow-[0_0_16px_rgba(167,139,250,0.4)]",
  },
  {
    id: "bc", kind: "link", href: "/bpjeps-motonautisme#bc", icon: GraduationCap, label: "BC 1·2·3",
    color: "text-fuchsia-400", activeBg: "bg-fuchsia-400/15", activeBorder: "border-fuchsia-400",
    activeGlow: "shadow-[0_0_16px_rgba(232,121,249,0.4)]",
  },
  {
    id: "ripam", kind: "link", href: "/bpjeps-motonautisme#ripam", icon: AlertTriangle, label: "RIPAM",
    color: "text-orange-400", activeBg: "bg-orange-400/15", activeBorder: "border-orange-400",
    activeGlow: "shadow-[0_0_16px_rgba(251,146,60,0.4)]",
  },
  {
    id: "esh", kind: "link", href: "/bpjeps-motonautisme#esh", icon: Zap, label: "ESH",
    color: "text-pink-400", activeBg: "bg-pink-400/15", activeBorder: "border-pink-400",
    activeGlow: "shadow-[0_0_16px_rgba(244,114,182,0.4)]",
  },
  {
    id: "epmsp", kind: "link", href: "/bpjeps-motonautisme#epmsp", icon: ClipboardCheck, label: "EPMSP",
    color: "text-emerald-400", activeBg: "bg-emerald-400/15", activeBorder: "border-emerald-400",
    activeGlow: "shadow-[0_0_16px_rgba(52,211,153,0.4)]",
  },
  {
    id: "meca", kind: "link", href: "/bpjeps-motonautisme#meca", icon: Wrench, label: "MECA",
    color: "text-cyan-400", activeBg: "bg-cyan-400/15", activeBorder: "border-cyan-400",
    activeGlow: "shadow-[0_0_16px_rgba(34,211,238,0.4)]",
  },
  {
    id: "meteo", kind: "link", href: "/meteo-marine-guide", icon: CloudSun, label: "Météo",
    color: "text-cyan-300", activeBg: "bg-cyan-300/15", activeBorder: "border-cyan-300",
    activeGlow: "shadow-[0_0_16px_rgba(103,232,249,0.4)]",
  },
  {
    id: "captain-pro", kind: "link", href: "/maritime-careers", icon: Crown, label: "Captain",
    color: "text-amber-400", activeBg: "bg-amber-400/15", activeBorder: "border-amber-400",
    activeGlow: "shadow-[0_0_16px_rgba(251,191,36,0.4)]",
  },
];

export const BottomActionBar = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <>
      {/* ─── Floating Action Bar ─── */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[60] bg-background/95 backdrop-blur-md border-t border-border/40 ${
          isMobile ? "" : "left-auto right-6 bottom-6 w-auto rounded-full border border-border/40 shadow-2xl"
        }`}
      >
        <div className={`flex items-center ${isMobile ? "px-2 py-1.5 gap-1 overflow-x-auto no-scrollbar" : "px-3 py-2 gap-1 justify-around"}`}>
          {actions.map((a) => {
            const baseClass = `flex flex-col items-center justify-center gap-0.5 rounded-xl transition-all duration-200 border border-transparent hover:bg-secondary/50 ${
              isMobile ? "min-w-[52px] min-h-[56px] px-1 py-1" : "min-w-[48px] min-h-[48px] px-3 py-1.5"
            }`;
            const inner = a.kind === "chat" ? (
              <CaptainLogo size={isMobile ? 24 : 22} />
            ) : (
              <a.icon className={`${a.color} ${isMobile ? "w-6 h-6" : "w-5 h-5"}`} />
            );
            const label = (
              <span className={`text-[10px] font-medium leading-none ${a.color}`}>{a.label}</span>
            );

            if (a.kind === "chat") {
              return (
                <button
                  key={a.id}
                  onClick={() => setChatOpen(true)}
                  className={baseClass}
                  aria-label="Open Captain ROCA chat"
                >
                  {inner}
                  {label}
                </button>
              );
            }

            return (
              <Link key={a.id} to={a.href} className={baseClass} aria-label={a.label}>
                {inner}
                {label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ─── Captain ROCA Full-Screen Chat ─── */}
      {chatOpen && (
        <div className="fixed inset-0 z-[70] bg-background/95 backdrop-blur-lg animate-fade-in">
          <button
            onClick={() => setChatOpen(false)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/30 hover:bg-secondary/50 transition-all"
            aria-label="Close chat"
          >
            <X size={20} className="text-foreground" />
          </button>
          <CaptainRocaAI onClose={() => setChatOpen(false)} />
        </div>
      )}
    </>
  );
};
